import React, { useMemo } from 'react';
import { MenuItem } from '@mui/material';
import MaterialSelector from 'components/Selector/MaterialSelector';
import {
    formatInvoiceHour,
    formatInvoiceTime,
    getInvoiceHourOptions,
    INVOICE_MINUTE_OPTIONS,
    parseInvoiceTime,
} from './invoiceTimeOptions';

interface InvoiceTimeSelectorProps {
    label: string;
    name: string;
    postedStart: string;
    value: string;
    disabled?: boolean;
    onChange: (value: string) => void;
}

const InvoiceTimeSelector: React.FC<InvoiceTimeSelectorProps> = ({ label, name, postedStart, value, disabled, onChange }) => {
    const selected = parseInvoiceTime(value);
    const hours = useMemo(() => {
        const options = getInvoiceHourOptions(postedStart);
        // A historical invoice can contain a time outside the current posting window.
        return selected && !options.includes(selected.hour)
            ? [selected.hour, ...options]
            : options;
    }, [postedStart, selected?.hour]);
    const minutes = useMemo(() => selected && !INVOICE_MINUTE_OPTIONS.includes(selected.minute)
        ? [selected.minute, ...INVOICE_MINUTE_OPTIONS].sort((a, b) => a - b)
        : INVOICE_MINUTE_OPTIONS, [selected?.minute]);

    const update = (part: 'hour' | 'minute', rawValue: string) => {
        const fallback = parseInvoiceTime(postedStart) || { hour: 9, minute: 0 };
        onChange(formatInvoiceTime({ ...fallback, ...(selected || {}), [part]: Number(rawValue) }));
    };

    return (
        <div role="group" aria-label={label} style={{ display: 'flex', gap: 8, minWidth: 0 }}>
            <div style={{ flex: '1 1 60%', minWidth: 0 }}>
                <MaterialSelector
                    name={`${name}Hour`}
                    label={`${label} hour`}
                    value={selected == null ? '' : String(selected.hour)}
                    disabled={disabled}
                    onChange={(event) => update('hour', String(event.target.value))}
                >
                    <MenuItem value="">Select hour</MenuItem>
                    {hours.map(hour => <MenuItem key={hour} value={String(hour)}>{formatInvoiceHour(hour)}</MenuItem>)}
                </MaterialSelector>
            </div>
            <div style={{ flex: '1 1 40%', minWidth: 0 }}>
                <MaterialSelector
                    name={`${name}Minute`}
                    label={`${label} minute`}
                    value={selected == null ? '' : String(selected.minute)}
                    disabled={disabled}
                    onChange={(event) => update('minute', String(event.target.value))}
                >
                    <MenuItem value="">Select minute</MenuItem>
                    {minutes.map(minute => <MenuItem key={minute} value={String(minute)}>{String(minute).padStart(2, '0')}</MenuItem>)}
                </MaterialSelector>
            </div>
        </div>
    );
};

export default InvoiceTimeSelector;
